import { useQuery } from "@tanstack/react-query";
import add from "date-fns/add";
import sub from "date-fns/sub";
import { getAggregates } from "queries/rating";
import React from "react";
import { useAppSelector } from "reduxUtils/hook";
import {
  getAggregatesTotalValue,
  getValidAggregateTotals,
} from "utils/aggregateCalculations/findValidAggregateTotals";
import { AVAILABLE_PERIODS } from "utils/constants";
import { NZD } from "utils/currency";
import { jwtClient } from "utils/jwtClient";

import { renderDeltaMessage } from "./DeltaMessage";
import { withErrorBoundary } from "./fallback/withErrorBoundary";
import { LoadingSection } from "./LoadingSection";

type LastPeriodComparisonProps = {
  daysThroughPeriod?: number;
  nominatedBillingPeriod: string;
  thisPeriodStartAt?: Date;
  thisPeriodValue?: number;
};

export const LastPeriodComparison =
  withErrorBoundary<LastPeriodComparisonProps>(
    ({
      thisPeriodValue,
      thisPeriodStartAt,
      daysThroughPeriod,
      nominatedBillingPeriod,
    }) => {
      const { supplyNodeRef } = useAppSelector((store) => store.currentAccount);
      const { periodName, duration } =
        AVAILABLE_PERIODS[nominatedBillingPeriod];
      const previousPeriodStart = sub(new Date(thisPeriodStartAt), {
        days: duration,
      });

      const { data: aggregatesData, isFetching: isAggregatesFetching } =
        useQuery<Aggregates, Error>(
          [
            "aggregates",
            {
              supplyNodeRef,
              startAt: previousPeriodStart,
              endAt: add(previousPeriodStart, { days: daysThroughPeriod }),
              jwtClient,
            },
          ],
          getAggregates,
          { useErrorBoundary: true },
        );

      if (isAggregatesFetching) return <LoadingSection />;

      const validAggregateTotals = getValidAggregateTotals(aggregatesData);

      const aggregatesTotalValue =
        getAggregatesTotalValue(validAggregateTotals);

      const delta = thisPeriodValue - aggregatesTotalValue;

      return (
        <div className="header_section">
          {renderDeltaMessage({
            delta,
            formatter: (v) => NZD.format(v),
            periodLabel: `this time last ${periodName}`,
          })}
        </div>
      );
    },
  );
